/**
 * ListViewHandler is a module in charge of rendering results in the grid where cards are shown to the user.
 */
import { store, store$ } from '@redux/store';
import { selectResultsPresentationType } from 'layout/redux/selectors';
import {
  searchParametersActions,
  selectSearchModifiers,
  getResults$,
  selectSearchParameters,
  selectListingCount,
  selectHomesCount,
} from 'features/searchResultsFacets';
import { catchError, distinctUntilChanged, filter, map, of, switchMap } from 'rxjs';
import ResultPresentationType from 'layout/constants/resultPresentationType';
import fastdom from 'fastdom';
import LoadingStatus from 'enums/loadingStatus';
import trackEvents from './eventTracking/totalDeliveryValue';
import onListViewPaint from '../redux/actions';
import {
  selectResultsLoadedAndResultsPresentationType,
  selectHasAnyResultTypeTabResults,
} from '../redux/selectors';
import hideHeaderSection from './noResults/handlerNoResultsSection';

/**
 * loadResults: inserts the more recent loaded results in the grid view.
 */
const loadResults = ({ presentationType, results }) => {
  const element = document.querySelector('[data-list-view-element]');

  if (presentationType === ResultPresentationType.InfiniteScrolling.toString()) {
    const result = document.createRange().createContextualFragment(results);
    element.appendChild(result);
  } else {
    element.innerHTML = results;
  }

  store.dispatch(onListViewPaint());
};

/**
 * loadingResult$ observes and emits a value every time we are loading results.
 */
const loadingResult$ = store$.pipe(
  map(selectResultsLoadedAndResultsPresentationType),
  distinctUntilChanged(),
  filter((information) => information.fetchListingStatus?.name === LoadingStatus.Loading.toString()),
  filter((information) => information.presentationType !== ResultPresentationType.InfiniteScrolling.toString())
);

loadingResult$.subscribe(() =>
  fastdom.mutate(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  })
);

/**
 * onLoadingResults$ observes and emits a value to determine when we have to trigger the ajax request in charge of
 * bringing new results based in the users' search criteria.
 */
const onLoadingResults$ = store$.pipe(
  map(selectSearchModifiers),
  distinctUntilChanged(),
  filter(({ fetchResultsListingStatus }) => fetchResultsListingStatus?.name === LoadingStatus.Loading.toString())
);

/**
 * onResultLoad$ observes and emits a value when new results are ready from server side.
 */
const onResultLoad$ = onLoadingResults$.pipe(
  switchMap(() => {
    const state = store.getState();
    const payload = {
      searchParameters: selectSearchParameters(state)
    };
    const presentationType = selectResultsPresentationType(state);
    const response = {
      presentationType,
    };

    return getResults$(payload).pipe(
      switchMap((data) => {
        response.results = data;

        loadResults(response);
        store.dispatch(searchParametersActions.fetchResultsListingFulfilled());

        return of(response);
      }),
      catchError((error) => {
        response.results = error;
        store.dispatch(searchParametersActions.fetchResultsListingError());

        return of(error);
      })
    );
  })
);

/**
 * Observable that emits counts from the store whenever relevant values change.
 * Uses distinctUntilChanged to only emit when listing/homes counts actually change,
 * preventing unnecessary updates for duplicate values.
 *
 * @type {Observable<Object>}
 * @fires {Object} The current counts from the store state
 */
const onCountsUpdated$ = store$.pipe(
  map((state) => ({
    listingCount: selectListingCount(state),
    homesCount: selectHomesCount(state),
    hasAnyResultTypeTabResults: selectHasAnyResultTypeTabResults(state),
  })),
  distinctUntilChanged((previous = {}, current = {}) =>
    previous?.listingCount === current?.listingCount &&
    previous?.homesCount === current?.homesCount &&
    previous?.hasAnyResultTypeTabResults === current?.hasAnyResultTypeTabResults
  ));

onCountsUpdated$.subscribe(hideHeaderSection);
onResultLoad$.subscribe(trackEvents);